/**
 * Der Call-to-Action-Button einer HTML-Box — EIN Bauteil für alle Stellen.
 *
 * Gezeichnet wird er auf der Pro-Editor-Bühne, in der Vorschau und im
 * veröffentlichten Scrolly. Drei eigene Fassungen wären die nächste Quelle
 * dafür, dass Editor und Leser Verschiedenes zeigen — genau das ist uns in
 * dieser Woche mehrfach passiert (Textformate, Box-Effekte, Branding der
 * Vorschau). Deshalb liegt er hier und wird überall importiert.
 *
 * Die Optik folgt dem START-Button des Startbildschirms (Jans Vorgabe
 * 05.08.2026: „quasi identisch"): Kontur färbt Umrandung UND Beschriftung,
 * Füllung ist optional, ein Bild ersetzt den Button vollständig.
 */
import type { CSSProperties, FunctionComponent } from "react";
import type { ResolvedCta } from "@scrolly/post-types";

interface Props {
    cta: ResolvedCta;
    /**
     * Im EDITOR soll ein Klick nicht die Seite verlassen — man arbeitet
     * gerade. Der Button sieht identisch aus, führt aber nirgendwohin.
     */
    editorMode?: boolean;
    /**
     * Ohne den Abstand nach oben. Der gehört zur HTML-Box (Knopf unter dem
     * Text); im MITLAUFENDEN CTA sitzt der Knopf mittig in einer Zeile, und
     * dort drückte ihn der Rand nach unten (Jans Fund 05.08.2026 — der
     * vorherige Gegen-Rand im Streifen verrechnete sich nicht sauber,
     * Ränder kollabieren durch rahmenlose Wrapper hindurch).
     */
    ohneAbstand?: boolean;
}

const farbe = (c: ResolvedCta["borderColor"]): string | undefined =>
    c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})` : undefined;

const CtaButton: FunctionComponent<Props> = ({ cta, editorMode, ohneAbstand }) => {
    const kontur = farbe(cta.borderColor) ?? "currentColor";
    const fuellung = farbe(cta.fillColor) ?? "transparent";

    const ausrichtung =
        cta.align === "left"
            ? "flex-start"
            : cta.align === "right"
              ? "flex-end"
              : "center";

    const inhalt = cta.bildUrl ? (
        <img
            src={cta.bildUrl}
            alt={cta.beschriftung || ""}
            /*
             * BREITE NACH DATEITYP (Jans Regel 05.08.2026):
             *   SVG        → feste Vorgabebreite. Vektoren bringen oft keine
             *                nutzbare Eigengröße mit; der Browser streckt sie
             *                sonst auf den ganzen Platz (sein Test: das Logo
             *                lief über die halbe Bühne).
             *   PNG/JPEG   → die echte Dateibreite, „px-Größe = Größe in der
             *                HTML-Box".
             * `bildBreite` entscheidet das in resolveCta, nicht hier — sonst
             * hätten Editor und Leser wieder zwei Meinungen.
             *
             * `maxWidth: 100%` immer: Kein Bild darf aus der Box laufen, auch
             * wenn die Datei breiter ist als der Platz.
             *
             * KEIN min-width mehr: Die „mindestens 160 px" im Hinweistext sind
             * ein RAT für die Datei, keine Vorschrift für die Anzeige. Als CSS
             * hätten sie ein kleines Motiv künstlich aufgeblasen — und Jans
             * Regel gerade widersprochen.
             */
            style={{
                display: "block",
                ...(cta.bildBreite ? { width: `${cta.bildBreite}px` } : {}),
                maxWidth: "100%",
                height: "auto",
            }}
        />
    ) : (
        cta.beschriftung
    );

    // Ein Bild ERSETZT den Button — dann kein Rahmen, keine Polsterung, keine
    // Füllung, sonst säße das Motiv in einer fremden Hülle.
    const alsBild = Boolean(cta.bildUrl);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: ausrichtung,
                marginTop: ohneAbstand ? 0 : "1em",
            }}
        >
            <a
                /*
                 * `scrolly-cta` ist KEINE Zierde, sondern der Anker fuer eine
                 * Ausnahme: In Template-Scrollys erzwingt der Player die
                 * Branding-Schrift auf ALLES in `.slide-content` — mit
                 * `!important`, was jede direkt gesetzte Schrift schlaegt. Die
                 * am Button gewaehlte Schrift kam deshalb nie an (Jans Fund
                 * 05.08.2026: „die Schrift bei dem CTA wird ueberhaupt nicht im
                 * Preview angezeigt"). Die Gegenregel steht im Player, direkt
                 * neben der Regel, die sie aushebelt.
                 */
                className="scrolly-cta"
                href={editorMode ? undefined : cta.href}
                target={editorMode ? undefined : cta.target}
                rel={editorMode ? undefined : cta.rel}
                // Im Editor ist der Klick bewusst wirkungslos, siehe `editorMode`.
                onClick={editorMode ? (e) => e.preventDefault() : undefined}
                style={{
                    display: "inline-block",
                    textDecoration: "none",
                    cursor: editorMode ? "default" : "pointer",
                    ...(alsBild
                        ? {}
                        : {
                              color: kontur,
                              border: `${cta.borderWidth}px solid ${kontur}`,
                              borderRadius: `${cta.radius}px`,
                              background: fuellung,
                              padding: "0.5em 1.4em",
                              // gewählter Schnitt, sonst SemiBold wie bisher (resolveCta)
                              fontWeight: cta.fontWeight,
                              lineHeight: 1.2,
                              /*
                               * ZWEIMAL dieselbe Schrift, mit Absicht:
                               *   `fontFamily`  wirkt ueberall dort, wo keine
                               *                 !important-Regel dazwischenfunkt
                               *                 (Pro-Editor-Buehne).
                               *   die Variable  fuettert die Gegenregel im
                               *                 Player, die in Template-Scrollys
                               *                 noetig ist.
                               * Ohne beides fiele je nach Umgebung eine der
                               * beiden Anzeigen zurueck auf die Branding-Schrift.
                               */
                              ...(cta.fontFamily
                                  ? ({
                                        fontFamily: `'${cta.fontFamily}', sans-serif`,
                                        "--scrolly-cta-font": `'${cta.fontFamily}', sans-serif`,
                                    } as CSSProperties)
                                  : {}),
                          }),
                }}
            >
                {inhalt}
            </a>
        </div>
    );
};

export default CtaButton;
